import { isDefined } from "@clipboard-health/util-ts";
import { FullScreenDialog } from "@src/appV2/lib";
import { APP_V2_USER_EVENTS, logEvent } from "@src/appV2/lib/analytics";
import { useLogEffect } from "@src/appV2/lib/analytics/useLogEffect";
import type { UseModalState } from "@src/appV2/redesign/system/ui-components";
import { useDefinedWorker } from "@src/appV2/Worker/useDefinedWorker";
import { useCallback, useEffect, useState } from "react";

import { WorkBadgeReviewPolicyStep } from "../../../../WorkBadges/hooks/useWorkBadgeReviewPolicy";
import {
  BadgeVerificationWindowDays,
  useGetWorkerBadgeVerifications,
} from "./api/useGetWorkerBadgeVerifications";
import { BadgeVerificationCameraPermissionSheet } from "./BadgeVerificationCameraPermissionSheet";
import { BadgeVerificationConfirmContentRedesign } from "./BadgeVerificationConfirmContentRedesign";
import { BadgeVerificationGradeResultContent } from "./BadgeVerificationGradeResultContent";
import { BadgeVerificationGradingContent } from "./BadgeVerificationGradingContent";
import { BadgeVerificationTakePhotoContent } from "./BadgeVerificationTakePhotoContent";
import { BadgeVerificationTimedOutContent } from "./BadgeVerificationTimedOutContent";
import { useBadgeVerificationPhotoFlow } from "./hooks/useBadgeVerificationPhotoFlow";
import { useBadgeVerificationSubmit } from "./hooks/useBadgeVerificationSubmit";
import { useCameraPermissionSheet } from "./hooks/useCameraPermissionSheet";
import { useNavigateToBadgePolicy } from "./hooks/useNavigateToBadgePolicy";
import { NoBadgeContentRedesign } from "./NoBadgeContentRedesign";
import { SkipBadgeVerificationContent } from "./SkipBadgeVerificationContent";
import { SkippedBadgeVerificationContent } from "./SkippedBadgeVerificationContent";
import { type BadgeVerificationPhoto, BadgeVerificationStep } from "./types";

const MAX_MISSED_BEFORE_RESTRICTION = 3;

interface BadgeVerificationDialogRedesignProps {
  shiftId: string;
  qualificationName: string;
  modalState: UseModalState;
  initialStep?: BadgeVerificationStep;
  onSubmitPhoto: () => void;
  onSkipBadgeVerification: () => void;
}

export function BadgeVerificationDialogRedesign(props: BadgeVerificationDialogRedesignProps) {
  const {
    shiftId,
    qualificationName,
    modalState,
    initialStep,
    onSubmitPhoto,
    onSkipBadgeVerification,
  } = props;

  const worker = useDefinedWorker();
  const { cameraPermissionModalState, onCameraPermissionDenied } = useCameraPermissionSheet();
  const { missedVerifications } = useGetWorkerBadgeVerifications(
    worker.userId,
    BadgeVerificationWindowDays.MISSED_BADGES,
    { includeMissedBadges: true, enabled: modalState.modalIsOpen }
  );
  const unverifiedBadgeCount = missedVerifications.length;

  const [step, setStep] = useState(initialStep ?? BadgeVerificationStep.INTRO);
  const [photo, setPhoto] = useState<BadgeVerificationPhoto | undefined>(undefined);

  useEffect(() => {
    if (isDefined(initialStep)) {
      setStep(initialStep);
    }
  }, [initialStep]);

  const { handleSubmit, handleSkipToClockIn, isSubmitting } = useBadgeVerificationSubmit({
    shiftId,
    onSkipSuccess: () => {
      // After they skip it will be one higher than unverified badge count
      // because unverifiedBadgeCount is captured here and won't update when the request is sent
      if (unverifiedBadgeCount + 1 >= MAX_MISSED_BEFORE_RESTRICTION) {
        onSkipBadgeVerification();
      } else {
        setStep(BadgeVerificationStep.SKIP_RESULT);
      }
    },
  });

  const { takePhoto, handleTimedOutSubmit, aiGradePolling, isUploadComplete } =
    useBadgeVerificationPhotoFlow({
      shiftId,
      step,
      photo,
      setStep,
      setPhoto,
      handleSubmit,
      onSubmitPhoto,
      onCameraPermissionDenied,
    });

  useLogEffect(
    APP_V2_USER_EVENTS.BADGE_VERIFICATION_DIALOG_VIEWED,
    { shiftId },
    { enabled: modalState.modalIsOpen }
  );

  const navigateToBadgePolicyStep = useNavigateToBadgePolicy({
    shiftId,
    qualificationName,
    currentStep: step,
  });

  const handleClose = useCallback(() => {
    modalState.closeModal();
    setStep(BadgeVerificationStep.INTRO);
    setPhoto(undefined);
  }, [modalState]);

  function renderStep() {
    switch (step) {
      case BadgeVerificationStep.INTRO:
        return (
          <BadgeVerificationTakePhotoContent
            isSubmitting={isSubmitting}
            onBack={handleClose}
            onTakePhoto={() => {
              logEvent(APP_V2_USER_EVENTS.BADGE_VERIFICATION_TAKE_PHOTO_TAPPED, { shiftId });
              void takePhoto();
            }}
            onNoBadge={() => {
              logEvent(APP_V2_USER_EVENTS.BADGE_VERIFICATION_NO_BADGE_TAPPED, { shiftId });
              setStep(BadgeVerificationStep.NO_BADGE);
            }}
            onWhyBadgeRequired={() => {
              logEvent(APP_V2_USER_EVENTS.BADGE_VERIFICATION_WHY_BADGE_REQUIRED_TAPPED, {
                shiftId,
              });
              navigateToBadgePolicyStep(WorkBadgeReviewPolicyStep.REVIEW_POLICY);
            }}
          />
        );

      case BadgeVerificationStep.GRADING:
        return <BadgeVerificationGradingContent />;

      case BadgeVerificationStep.GRADING_TIMED_OUT:
        if (!isDefined(photo)) {
          return undefined;
        }

        return (
          <BadgeVerificationTimedOutContent
            title="It is taking longer than usual"
            photoPreviewUrl={photo.previewUrl}
            isSubmitting={false}
            onRetakePhoto={() => {
              logEvent(APP_V2_USER_EVENTS.BADGE_VERIFICATION_RETAKE_PHOTO_TAPPED, { shiftId });
              void takePhoto();
            }}
            onSubmitAndClockIn={handleTimedOutSubmit}
          />
        );

      case BadgeVerificationStep.CONFIRM:
        if (!isDefined(photo)) {
          return undefined;
        }

        if (isDefined(aiGradePolling.gradeResult)) {
          return (
            <BadgeVerificationGradeResultContent
              photoPreviewUrl={photo.previewUrl}
              gradeResult={aiGradePolling.gradeResult}
              isSubmitting={isSubmitting}
              onClockIn={onSubmitPhoto}
              onForceSubmit={() => {
                logEvent(APP_V2_USER_EVENTS.BADGE_VERIFICATION_FORCE_SUBMITTED, { shiftId });
                onSubmitPhoto();
              }}
              onRetakePhoto={() => {
                logEvent(APP_V2_USER_EVENTS.BADGE_VERIFICATION_RETAKE_PHOTO_TAPPED, { shiftId });
                void takePhoto();
              }}
            />
          );
        }

        if (isUploadComplete) {
          return (
            <BadgeVerificationTimedOutContent
              title="Thank you for verifying your badge"
              photoPreviewUrl={photo.previewUrl}
              isSubmitting={false}
              onRetakePhoto={() => {
                logEvent(APP_V2_USER_EVENTS.BADGE_VERIFICATION_RETAKE_PHOTO_TAPPED, { shiftId });
                void takePhoto();
              }}
              onSubmitAndClockIn={onSubmitPhoto}
            />
          );
        }

        return (
          <BadgeVerificationConfirmContentRedesign
            photoPreviewUrl={photo.previewUrl}
            isSubmitting={isSubmitting}
            onBack={() => {
              setStep(BadgeVerificationStep.INTRO);
              setPhoto(undefined);
            }}
            onSubmit={() => {
              if (isDefined(photo)) {
                void handleSubmit(photo);
              }
            }}
            onRetakePhoto={() => {
              logEvent(APP_V2_USER_EVENTS.BADGE_VERIFICATION_RETAKE_PHOTO_TAPPED, { shiftId });
              void takePhoto();
            }}
          />
        );

      case BadgeVerificationStep.NO_BADGE:
        return (
          <NoBadgeContentRedesign
            isSubmitting={isSubmitting}
            onBack={() => {
              logEvent(APP_V2_USER_EVENTS.BADGE_VERIFICATION_HAS_BADGE_TAPPED, { shiftId });
              setStep(BadgeVerificationStep.INTRO);
            }}
            onPickedUpBadge={() => setStep(BadgeVerificationStep.INTRO)}
            onSkipToClockIn={() => setStep(BadgeVerificationStep.SKIP_CONFIRM)}
          />
        );

      case BadgeVerificationStep.SKIP_CONFIRM:
        return (
          <SkipBadgeVerificationContent
            isSubmitting={isSubmitting}
            onBack={() => setStep(BadgeVerificationStep.NO_BADGE)}
            onConfirmSkip={handleSkipToClockIn}
            onGetTemporaryBadge={() => setStep(BadgeVerificationStep.NO_BADGE)}
          />
        );

      case BadgeVerificationStep.SKIP_RESULT:
        return (
          <SkippedBadgeVerificationContent
            unverifiedBadgeCount={unverifiedBadgeCount}
            onClockIn={onSkipBadgeVerification}
            onViewBadgePolicy={() =>
              navigateToBadgePolicyStep(WorkBadgeReviewPolicyStep.REVIEW_POLICY)
            }
            onNeedNewBadge={() =>
              navigateToBadgePolicyStep(WorkBadgeReviewPolicyStep.SELECT_HEADSHOT)
            }
          />
        );

      default: {
        const exhaustiveCheck: never = step;
        return exhaustiveCheck;
      }
    }
  }

  return (
    <>
      <FullScreenDialog
        skipTopSafeAreaPadding
        modalState={modalState}
        variant="tertiary"
        transitionDuration={0}
      >
        {renderStep()}
      </FullScreenDialog>
      <BadgeVerificationCameraPermissionSheet modalState={cameraPermissionModalState} />
    </>
  );
}
